import { useState, useCallback, type ReactNode } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { GripVertical } from "lucide-react";

/**
 * Item interface for SortableList
 */
export interface SortableItem {
  id: string | number;
}

/**
 * Props for the render function
 */
export interface SortableItemRenderProps<T extends SortableItem> {
  item: T;
  index: number;
  isDragging: boolean;
  dragHandleProps: {
    ref: (node: HTMLElement | null) => void;
    style: React.CSSProperties;
  };
}

/**
 * Props for SortableList component
 */
interface SortableListProps<T extends SortableItem> {
  items: T[];
  onReorder: (items: T[]) => void;
  renderItem: (props: SortableItemRenderProps<T>) => ReactNode;
  renderOverlay?: (item: T) => ReactNode;
  className?: string;
  itemClassName?: string;
  disabled?: boolean;
}

/**
 * Internal sortable item wrapper
 */
interface SortableItemWrapperProps<T extends SortableItem> {
  item: T;
  index: number;
  renderItem: (props: SortableItemRenderProps<T>) => ReactNode;
  itemClassName?: string;
  disabled?: boolean;
}

function SortableItemWrapper<T extends SortableItem>({
  item,
  index,
  renderItem,
  itemClassName,
  disabled,
}: SortableItemWrapperProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  const dragHandleProps = {
    ref: setActivatorNodeRef,
    style: { cursor: disabled ? "default" : "grab" } as React.CSSProperties,
    ...attributes,
    ...listeners,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("relative", isDragging && "z-50", itemClassName)}
    >
      {renderItem({
        item,
        index,
        isDragging,
        dragHandleProps,
      })}
    </div>
  );
}

/**
 * SortableList component using @dnd-kit
 *
 * Provides drag-and-drop reordering for a list of items.
 * Features drag handles, visual feedback, and optimistic updates.
 *
 * @example
 * ```tsx
 * <SortableList
 *   items={modules}
 *   onReorder={handleReorder}
 *   renderItem={({ item, dragHandleProps }) => (
 *     <div className="flex items-center gap-2 p-4 bg-card rounded-lg">
 *       <div {...dragHandleProps}>
 *         <GripVertical className="h-4 w-4" />
 *       </div>
 *       <span>{item.name}</span>
 *     </div>
 *   )}
 * />
 * ```
 */
export function SortableList<T extends SortableItem>({
  items,
  onReorder,
  renderItem,
  renderOverlay,
  className,
  itemClassName,
  disabled = false,
}: SortableListProps<T>) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(items, oldIndex, newIndex);
        onReorder(newItems);
      }
    },
    [items, onReorder]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const activeItem = activeId
    ? items.find((item) => item.id === activeId)
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={items.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
        disabled={disabled}
      >
        <div className={cn("space-y-2", className)}>
          {items.map((item, index) => (
            <SortableItemWrapper
              key={item.id}
              item={item}
              index={index}
              renderItem={renderItem}
              itemClassName={itemClassName}
              disabled={disabled}
            />
          ))}
        </div>
      </SortableContext>

      <DragOverlay>
        {activeItem && renderOverlay ? (
          renderOverlay(activeItem)
        ) : (activeItem ? (
          <div className="opacity-80 shadow-lg">
            {renderItem({
              item: activeItem,
              index: items.findIndex((i) => i.id === activeItem.id),
              isDragging: true,
              dragHandleProps: {
                ref: () => {},
                style: { cursor: "grabbing" },
              },
            })}
          </div>
        ) : null)}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * DragHandle component for convenience
 */
interface DragHandleProps {
  className?: string;
  disabled?: boolean;
}

export function DragHandle({ className, disabled }: DragHandleProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-center p-1 rounded hover:bg-muted transition-colors",
        disabled
          ? "cursor-not-allowed opacity-50"
          : "cursor-grab active:cursor-grabbing",
        className
      )}
    >
      <GripVertical className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}
